import * as React from 'react';

import { useNewbuildingBrokerAnalyticsUser, useNewbuildingBrokerEnabled } from './hooks';
import { trackSellApartmentClick } from './tracking';
import { getPartnerRewardsLanding } from './utils/getPartnerRewardsLanding';
import { NewbuildingBrokerButton } from '../../components/NewbuildingBrokerButton';
import { EDeviceType } from '../../types/device';
import { INewbuildingBroker, TNewbuildingBrokerFlow } from '../../types/newbuildingBroker';
import { toSiteType } from '../../utils/trackings';
import { NewbuildingBrokerWidgetContext } from '../NewbuildingBrokerWidgetProvider';

interface INewbuildingBrokerButtonContainerProps {
  newbuildingBroker: INewbuildingBroker | null | undefined;
  newbuildingId: number;
  houseId?: number | null;
  regionId?: number | null;
  deviceType: EDeviceType;
}

export function NewbuildingBrokerButtonContainer({
  newbuildingBroker,
  newbuildingId,
  regionId,
  houseId,
  deviceType,
}: INewbuildingBrokerButtonContainerProps) {
  const flowType = newbuildingBroker?.availableBrokerFlow || undefined;
  const isNeedSelfEmployedConfirmation = newbuildingBroker?.isNeedSelfEmployedConfirmation || false;
  const partnerRewardsLanding = getPartnerRewardsLanding(newbuildingBroker);

  const openWidget = React.useContext(NewbuildingBrokerWidgetContext);

  const { enabled } = useNewbuildingBrokerEnabled();
  const user = useNewbuildingBrokerAnalyticsUser(flowType);

  const [loading, setLoading] = React.useState(false);

  const openLandingOrWidget = React.useCallback(async () => {
    if (partnerRewardsLanding) {
      window.open(partnerRewardsLanding, '_blank', 'noopener');
    } else {
      setLoading(true);

      await openWidget({
        flowType: flowType as TNewbuildingBrokerFlow,
        newbuildingId,
        newbuildingRegionId: regionId || null,
        houseId: houseId || null,
        isSelfEmployed: isNeedSelfEmployedConfirmation,
        trackingParams: {
          pageType: 'ListingFavorites',
          siteType: toSiteType(deviceType),
          user,
        },
      });

      setLoading(false);
    }
  }, [
    deviceType,
    flowType,
    houseId,
    isNeedSelfEmployedConfirmation,
    newbuildingId,
    openWidget,
    partnerRewardsLanding,
    regionId,
    user,
  ]);

  const handleButtonClick = React.useCallback(async () => {
    trackSellApartmentClick(user, deviceType);
    await openLandingOrWidget();
  }, [deviceType, openLandingOrWidget, user]);

  if (!enabled || !flowType) {
    return null;
  }

  return <NewbuildingBrokerButton loading={loading} onClick={handleButtonClick} />;
}
